import { useState, type ReactNode } from "react";
import { recordedLabel } from "./format";
import { countsByView, groupByView, HISTORY_VIEWS, type CodeLabels, type HistoryView } from "./historyGroups";
import type { ResinContext } from "./types";

interface Props<Item extends CodeLabels> {
  items: Item[];
  resin: ResinContext;
  noun: string;
  isCompact: boolean;
  recordedUtcOf: (item: Item) => string | undefined;
  renderItems: (items: Item[]) => ReactNode;
}

export function CodeGroupedHistory<Item extends CodeLabels>({
  items,
  resin,
  noun,
  isCompact,
  recordedUtcOf,
  renderItems,
}: Props<Item>) {
  const [view, setView] = useState<HistoryView>("designation");
  const { resinFamilyCode } = resin;
  const counts = countsByView(items);
  const groups = groupByView(items, view);
  const viewSpec = HISTORY_VIEWS.find((candidate) => candidate.key === view)!;

  const readsAsFor = (code: string): string => {
    if (view === "designation") {
      return (
        resin.resinCodes.designations.find(({ designation }) => designation.designationCode === code)
          ?.designation.readsAs ?? ""
      );
    }
    if (viewSpec.slot) {
      return resin.registryCodes[viewSpec.slot].find((approvedCode) => approvedCode.code === code)?.readsAs ?? "";
    }
    return "";
  };

  const emptyText =
    view === "none"
      ? `Every ${noun} this month has a code picked.`
      : `No ${noun}s logged by ${viewSpec.label(resinFamilyCode).toLowerCase()} this month.`;

  return (
    <div className="flex flex-col gap-3">
      <div
        className={`seg max-w-full ${isCompact ? "flex-wrap" : "w-fit"}`}
        role="group"
        aria-label="Group history by"
      >
        {HISTORY_VIEWS.map((viewOption) => (
          <button
            key={viewOption.key}
            type="button"
            aria-pressed={view === viewOption.key}
            className={`inline-flex items-center justify-center gap-1.5 whitespace-nowrap ${
              isCompact ? "px-2! py-1.5! text-[11.5px]!" : "px-3! py-[7px]! text-[12.5px]!"
            }`}
            onClick={() => setView(viewOption.key)}
          >
            {viewOption.label(resinFamilyCode)}
            <span className="text-[11px] border border-current rounded-[3px] px-1.5 tabular-nums opacity-80">
              {counts[viewOption.key]}
            </span>
          </button>
        ))}
      </div>

      {groups.length === 0 ? (
        <p className="m-0 text-rs-light-gray text-[13px]">{emptyText}</p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {groups.map((group, index) => {
            const count = group.items.length;
            return (
              <details
                key={`${view}-${group.code}`}
                className="group border border-rs-jet rounded-md open:border-rs-jet-light"
                open={index === 0}
              >
                <summary
                  className={`list-none cursor-pointer grid items-center gap-x-2.5 gap-y-1 px-3.5 py-2.5 text-[13.5px] [&::-webkit-details-marker]:hidden ${
                    isCompact ? "grid-cols-[18px_minmax(0,1fr)]" : "grid-cols-[18px_minmax(0,1fr)_auto]"
                  }`}
                >
                  <span className="material-symbols-sharp text-[18px] text-rs-light-gray transition-transform group-open:rotate-90">
                    chevron_right
                  </span>
                  <span className="min-w-0 flex items-baseline gap-2.5">
                    {view === "none" ? (
                      <>
                        <span className="font-semibold text-rs-off-white whitespace-nowrap">All {resinFamilyCode}</span>
                        <span className="text-rs-light-gray truncate">no code picked</span>
                      </>
                    ) : (
                      <>
                        <code className="text-rs-green whitespace-nowrap">{group.code}</code>
                        <span className="text-rs-light-gray truncate">{readsAsFor(group.code)}</span>
                      </>
                    )}
                  </span>
                  <span
                    className={`flex items-center gap-2.5 text-xs text-rs-light-gray whitespace-nowrap ${
                      isCompact ? "col-start-2" : ""
                    }`}
                  >
                    <span>Latest {recordedLabel(recordedUtcOf(group.items[0]))}</span>
                    <span className="border border-rs-jet-light rounded-[3px] px-1.5 text-rs-off-white tabular-nums">
                      {count} {count === 1 ? noun : `${noun}s`}
                    </span>
                  </span>
                </summary>
                <div className={`pb-3 ${isCompact ? "px-3" : "px-3.5 sm:pl-10"}`}>{renderItems(group.items)}</div>
              </details>
            );
          })}
        </div>
      )}
    </div>
  );
}
